import { Field } from '@web/components/atoms/Field'
import { Input } from '@web/components/atoms/Input'
import { Separator, Stack, VStack } from '@web/components/atoms/Layout'
import { Textarea } from '@web/components/atoms/Textarea'
import CustomFieldComponent from '@web/components/page-components/CustomFields'
import PageCheckBox from '@web/components/page-components/DonationForm/components/atoms/PageCheckBox'
import AddressField from '@web/components/page-components/DonationForm/components/molecules/AddressField'
import MobileField from '@web/components/page-components/DonationForm/components/molecules/MobileField'
import { CustomFieldElementType, ExtraField, ExtraFieldType } from '@web/graphql-types'
import React, { useMemo } from 'react'
import { ExtraFieldsContext, useExtraFieldsContext } from './ExtraFieldsContext'

type ProfileSelectorProps = {
  label: string
  isRequired?: boolean
  placeholder?: string | null
}

export type ExtraFieldsProps = {
  fields: ExtraField[]
  values?: { [key: string]: string }
  onChange?: (name: string, value: string) => void
  googleMapsApiKey?: string
  address?: ExtraFieldsStateAddress
  onAddressChange?: (address: NonNullable<ExtraFieldsStateAddress>) => void
  includeProfileFields?: boolean
  showRemainAnonymous?: boolean
  Components?: {
    ProfileSelector: React.FC<ProfileSelectorProps>
  }
}

type ExtraFieldsStateAddress = React.ComponentProps<typeof AddressField>['address']

const getFieldValue = (field: ExtraField, values: { [key: string]: string } | undefined) => {
  const stored =
    field.customField?.id && field.type !== ExtraFieldType.Comments
      ? values?.[field.customField.id]
      : values?.[field.name]

  return stored ?? ''
}

const getInputFromFieldType = (
  field: ExtraField,
  onChange: (name: string, value: string) => void,
  value: string | undefined,
  Components?: ExtraFieldsProps['Components'],
) => {
  const { name, type, label, isRequired, placeholder, customField } = field
  const inputProps = {
    id: name,
    name,
    label,
    required: isRequired,
    placeholder,
    description: customField?.fieldDescription,
    onChange: (e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement | HTMLSelectElement>) =>
      onChange(e.target.name, e.target.value),
    value: value ?? '',
  }
  const fieldProps = { label, required: isRequired }

  switch (type) {
    case ExtraFieldType.Comments: {
      return (
        <Field {...fieldProps}>
          <Textarea
            {...inputProps}
            placeholder="Enter a message of support"
            bgColor="ge.white"
            color="ge.text.primary"
          />
        </Field>
      )
    }
    case ExtraFieldType.Phone: {
      return <MobileField {...inputProps} placeholder={`Enter ${name.toLowerCase()}`} />
    }
    case ExtraFieldType.Email: {
      return (
        <Field {...fieldProps}>
          <Input {...inputProps} type="email" placeholder={`Enter ${name.toLowerCase()}`} />
        </Field>
      )
    }
    case ExtraFieldType.Custom: {
      if (!customField || !customField.id) {
        return null
      }

      return <CustomFieldComponent {...inputProps} {...field} {...customField} />
    }
    case ExtraFieldType.Profile: {
      if (!Components?.ProfileSelector) {
        return null
      }

      return (
        <Components.ProfileSelector label={field.label} isRequired={field.isRequired} placeholder={field.placeholder} />
      )
    }
    case ExtraFieldType.DedicateTo: {
      return (
        <VStack w="100%" gap="16px" alignItems="stretch">
          <Separator w="100%" borderColor="#718096" />
          <CustomFieldComponent
            {...inputProps}
            {...field}
            elementType={CustomFieldElementType.Switch}
            customField={{
              id: 'should-dedicate',
              name: 'shouldDedicate',
              elementType: CustomFieldElementType.Switch,
            }}
            conditionalFields={[
              {
                type: ExtraFieldType.Custom,
                name: 'dedicateeName',
                label: field.conditionalFields?.[0]?.label ?? 'In Honour Of',
                placeholder: `Dedicatee's Name`,
                isRequired: true,
                customField: {
                  id: 'dedicateeName',
                  name: 'dedicateeName',
                  elementType: CustomFieldElementType.InputText,
                },
              },
              ...(field.conditionalFields?.[1]
                ? [
                    {
                      type: ExtraFieldType.Custom,
                      name: 'shouldNotifyDedicateeEmail',
                      label: 'Notify someone about this gift',
                      isRequired: false,
                      defaultValue: 'true',
                      customField: {
                        id: 'shouldNotifyDedicateeEmail',
                        name: 'shouldNotifyDedicateeEmail',
                        elementType: CustomFieldElementType.Checkbox,
                      },
                      conditionalFields: [
                        {
                          type: ExtraFieldType.Email,
                          name: 'dedicateeEmail',
                          label: 'Notification Email',
                          hideLabel: true,
                          placeholder: `Email Address`,
                          isRequired: true,
                          customField: {
                            id: 'dedicateeEmail',
                            name: 'dedicateeEmail',
                            elementType: CustomFieldElementType.InputText,
                          },
                        },
                      ],
                    },
                  ]
                : []),
            ]}
          />
          <Separator w="100%" borderColor="#718096" />
        </VStack>
      )
    }
    default: {
      return (
        <Field {...fieldProps}>
          <Input {...inputProps} placeholder={`Enter ${label.toLowerCase()}`} />
        </Field>
      )
    }
  }
}

export const ExtraFieldComponent = (
  props: ExtraField & {
    Components?: ExtraFieldsProps['Components']
    showRemainAnonymous?: boolean
  },
) => {
  const { Components, showRemainAnonymous = true, ...field } = props
  const { values, onChange, googleMapsApiKey, address, onAddressChange } = useExtraFieldsContext()

  return (
    <React.Fragment key={field.name}>
      <Stack direction="column" w="100%">
        {field.type === ExtraFieldType.Address && googleMapsApiKey ? (
          <AddressField
            apiKey={googleMapsApiKey}
            onChange={(fullAddress, nextAddress) => {
              onChange(field.customField?.id ?? field.name, fullAddress)
              onAddressChange?.(nextAddress)
            }}
            value={values[field.name] ?? ''}
            address={address}
            isRequired={field.isRequired}
          />
        ) : (
          getInputFromFieldType(field, onChange, getFieldValue(field, values), Components)
        )}
        {field.type === ExtraFieldType.Comments && showRemainAnonymous && (
          <PageCheckBox
            id="remain-anonymous"
            label={field.customField?.fieldDescription || 'Remain anonymous on donation page'}
            isChecked={values.isAnonymous === 'true'}
            onChange={(value) => onChange('isAnonymous', value ? 'true' : 'false')}
          />
        )}
      </Stack>
    </React.Fragment>
  )
}

const ExtraFields = ({
  fields,
  values = {},
  onChange = () => undefined,
  googleMapsApiKey,
  address,
  onAddressChange,
  includeProfileFields = false,
  showRemainAnonymous = true,
  Components,
}: ExtraFieldsProps) => {
  const visibleFields = useMemo(() => {
    if (includeProfileFields) {
      return fields
    }

    return fields.filter((field) => field.type !== ExtraFieldType.Profile)
  }, [fields, includeProfileFields])

  return (
    <ExtraFieldsContext.Provider value={{ values, onChange, googleMapsApiKey, address, onAddressChange }}>
      {visibleFields
        .filter((field) => !field.isHidden)
        .map((field) => (
          <ExtraFieldComponent
            key={field.name}
            {...field}
            Components={Components}
            showRemainAnonymous={showRemainAnonymous}
          />
        ))}
    </ExtraFieldsContext.Provider>
  )
}

export default ExtraFields
